import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FileText,
  Mail,
  Phone,
  User,
  MapPin,
  Calendar,
  Building,
  DollarSign,
  Clock,
  ChevronLeft,
  Paperclip
} from 'lucide-react';
import { fetchJobApplicationDetails, updateJobApplication} from '../redux/JobApplicationsSlice';
import { formatDate } from '../utils/Config';

const APPLICATION_STATUSES = ['pending', 'shortlisted', 'rejected', 'hired'];

const StatusBadge = ({ status }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'shortlisted':
        return 'bg-blue-100 text-blue-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'hired':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(status)}`}>
      {status}
    </span>
  );
};

const JobApplicationDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { selectedApplication: application, loading, error } = useSelector(state => state.jobApplications);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    dispatch(fetchJobApplicationDetails(id))
      .unwrap()
      .catch((error) => {
        toast.error(error.message || 'Failed to fetch application details');
      });
  }, [dispatch, id]);

  useEffect(() => {
    if (application?.notes) {
      setNotes(application.notes);
    }
  }, [application]);

  const handleStatusChange = async (newStatus) => {
    try {
      await dispatch(updateJobApplication({
        id,
        data: { status: newStatus }
      })).unwrap();
      toast.success('Application status updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update status');
    }
  };

  const handleUpdateNotes = async () => {
    try {
      await dispatch(updateJobApplication({
        id,
        data: { notes }
      })).unwrap();
      toast.success('Notes updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update notes');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
      </div>
    );
  }

  if (!application) return null;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <button
        onClick={() => navigate('/dashboard/job-applications')}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
      >
        <ChevronLeft className="h-5 w-5 mr-1" />
        Back to Applications
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Applicant Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Applicant Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center">
                <User className="h-5 w-5 text-gray-400 mr-2" />
                <div>
                  <div className="text-sm text-gray-500">Full Name</div>
                  <div className="font-medium">
                    {`${application.user.first_name} ${application.user.middle_name || ''} ${application.user.last_name}`}
                  </div>
                </div>
              </div>
              <div className="flex items-center">
                <Mail className="h-5 w-5 text-gray-400 mr-2" />
                <div>
                  <div className="text-sm text-gray-500">Email</div>
                  <div className="font-medium">{application.email}</div>
                </div>
              </div>
              <div className="flex items-center">
                <Phone className="h-5 w-5 text-gray-400 mr-2" />
                <div>
                  <div className="text-sm text-gray-500">Phone</div>
                  <div className="font-medium">{application.phone}</div>
                </div>
              </div>
              <div className="flex items-center">
                <Clock className="h-5 w-5 text-gray-400 mr-2" />
                <div>
                  <div className="text-sm text-gray-500">Applied On</div>
                  <div className="font-medium">{formatDate(application.created_at)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Job Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Job Details</h2>
            <div className="space-y-4">
              <div className="flex items-start">
                <Building className="h-5 w-5 text-gray-400 mr-2 mt-1" />
                <div>
                  <div className="text-sm text-gray-500">Position</div>
                  <div className="font-medium">{application.job_posting.title}</div>
                  <div className="text-sm text-gray-500">{application.job_posting.company_name}</div>
                </div>
              </div>
              <div className="flex items-start">
                <MapPin className="h-5 w-5 text-gray-400 mr-2 mt-1" />
                <div>
                  <div className="text-sm text-gray-500">Location</div>
                  <div className="font-medium">
                    {application.job_posting.location}
                    {application.job_posting.is_remote && " (Remote)"}
                  </div>
                </div>
              </div>
              <div className="flex items-start">
                <DollarSign className="h-5 w-5 text-gray-400 mr-2 mt-1" />
                <div>
                  <div className="text-sm text-gray-500">Salary Range</div>
                  <div className="font-medium">
                    {`$${application.job_posting.salary_min.toLocaleString()} - $${application.job_posting.salary_max.toLocaleString()}`}
                  </div>
                </div>
              </div>
              <div className="flex items-start">
                <Calendar className="h-5 w-5 text-gray-400 mr-2 mt-1" />
                <div>
                  <div className="text-sm text-gray-500">Application Deadline</div>
                  <div className="font-medium">
                    {formatDate(application.job_posting?.application_deadline)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Documents</h2>
            <div className="space-y-6">
              {application.resume && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 text-gray-400 mr-2" />
                    <span>Resume</span>
                  </div>
                  <a
                    href={application.resume}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 flex items-center"
                  >
                    <Paperclip className="h-4 w-4 mr-1" />
                    View
                  </a>
                </div>
              )}
              {application.cover_letter && (
                <div className="flex-col items-center justify-between">
                  <div className="flex items-center mt-4 py-2 border-b">
                    <FileText className="h-5 w-5 text-gray-400 mr-2" />
                    <span>Cover Letter</span>
                  </div>
                 <p className='p-2'>
                    {application.cover_letter}
                 </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Application Status</h2>
            <div className="space-y-4">
              <div className="flex justify-center">
                <StatusBadge status={application.status} />
              </div>
              <select
                value={application.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="w-full mt-2 p-2 rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
              >
                {APPLICATION_STATUSES.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Notes</h2>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full h-40 p-4 rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Add notes about this application..."
            />
            <button
              onClick={handleUpdateNotes}
              className="mt-4 w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Save Notes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobApplicationDetail;